import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { PageHero } from "@/components/Bits";

function Blocks({ blocks }) {
  return (
    <div className="space-y-4">
      {blocks.map((b, i) => {
        if (b.ul) {
          return (
            <ul key={i} className="space-y-2.5">
              {b.ul.map((item, j) => (
                <li key={j} className="flex items-start gap-3 text-[0.95rem] leading-relaxed text-muted-foreground">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-gold" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          );
        }
        if (b.contact) {
          const c = b.contact;
          return (
            <div key={i} className="rounded-xl border border-border bg-background/40 p-5">
              <p className="font-heading text-base font-semibold text-foreground">{c.name}</p>
              <p className="text-sm text-muted-foreground">{c.aka}</p>
              <p className="mt-2 text-sm text-muted-foreground">
                Email:{" "}
                <a href={`mailto:${c.email}`} className="text-gold underline underline-offset-2 hover:opacity-80">{c.email}</a>
              </p>
              <p className="text-sm text-muted-foreground">
                Website:{" "}
                <a href={c.website} target="_blank" rel="noreferrer" className="text-gold underline underline-offset-2 hover:opacity-80">{c.website}</a>
              </p>
            </div>
          );
        }
        return <p key={i} className="text-[0.95rem] leading-relaxed text-muted-foreground">{b.p}</p>;
      })}
    </div>
  );
}

function Section({ section, open, onToggle }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card transition-colors hover:border-gold/40" data-testid={`legal-section-${section.n}`}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        data-testid={`legal-toggle-${section.n}`}
        className="flex w-full items-center gap-4 px-6 py-5 text-left"
      >
        <span className="font-heading text-sm font-semibold text-gold">{section.n}</span>
        <span className="flex-1 font-heading text-lg font-semibold tracking-tight text-foreground">{section.title}</span>
        <ChevronDown className={`h-5 w-5 shrink-0 text-gold transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            style={{ overflow: "hidden" }}
          >
            <div className="border-t border-border px-6 pb-7 pt-5">
              <Blocks blocks={section.blocks} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function LegalDoc({ doc, crossLink }) {
  const [open, setOpen] = useState({});
  const allOpen = doc.sections.every((s) => open[s.n]);
  const toggle = (n) => setOpen((o) => ({ ...o, [n]: !o[n] }));
  const setAll = (val) => setOpen(Object.fromEntries(doc.sections.map((s) => [s.n, val])));

  return (
    <>
      <PageHero label={doc.label} title={doc.title} accent={doc.accent} subtitle={doc.subtitle} />
      <section className="mx-auto max-w-3xl px-5 py-20 md:px-8 md:py-24">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-border pb-6">
          <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground" data-testid="legal-dates">
            <span>Effective Date: <span className="text-foreground/80">{doc.effective}</span></span>
            <span className="mx-2 text-border">·</span>
            <span>Last Updated: <span className="text-foreground/80">{doc.updated}</span></span>
          </div>
          <button
            type="button"
            onClick={() => setAll(!allOpen)}
            data-testid="legal-expand-all"
            className="text-xs font-semibold uppercase tracking-[0.14em] text-gold transition-opacity hover:opacity-80"
          >
            {allOpen ? "Collapse all" : "Expand all"}
          </button>
        </div>

        <div className="mt-8 space-y-4 text-[0.95rem] leading-relaxed text-muted-foreground">
          {doc.intro.map((p, i) => <p key={i}>{p}</p>)}
        </div>

        <div className="mt-8 space-y-3">
          {doc.sections.map((s) => (
            <Section key={s.n} section={s} open={!!open[s.n]} onToggle={() => toggle(s.n)} />
          ))}
        </div>

        {crossLink && (
          <p className="mt-10 text-center text-sm text-muted-foreground" data-testid="legal-crosslink">
            {crossLink.pre}{" "}
            <a href={crossLink.to} className="text-gold underline underline-offset-2 hover:opacity-80">{crossLink.label}</a>.
          </p>
        )}
      </section>
    </>
  );
}
